import { useParams, Link } from 'react-router';
import { Carte, EnTetePage, ICONES_MODULE, Illustration, LIBELLES_THEMES, Message } from '@journal/ui';
import { LienBouton } from '../components/LienBouton.tsx';
import { PiedDePage } from '../components/PiedDePage.tsx';

/**
 * Documentation.
 *
 * ── Écrite ici, et non dans un site à part ──────────────────────────────────
 * Une aide en ligne hébergée ailleurs se périme sans qu'on s'en aperçoive :
 * elle décrit un écran qui a changé, et rien ne le signale. Ici, elle vit dans
 * le même dépôt que ce qu'elle décrit, et se relit au même moment.
 *
 * ── Pour qui elle est écrite ────────────────────────────────────────────────
 * Pour un enseignant, pas pour un développeur. Réécrite à la demande :
 * vouvoiement, des étapes numérotées « Comment faire… » qui citent les boutons
 * tels qu'ils s'affichent, puis quelques questions fréquentes. Aucun mot
 * technique — le test `documentation.test.tsx` en tient une liste noire.
 *
 * Chaque bouton cité a été relu dans l'écran correspondant : un libellé qui
 * change doit changer ici aussi, sans quoi l'étape envoie chercher un bouton
 * qui n'existe plus.
 *
 * ── Les captures ────────────────────────────────────────────────────────────
 * Prises sur une école FICTIVE (l'École des Marronniers) par
 * `tools/captures`, qui la remplit puis photographie chaque écran. Elles
 * vivent dans `assets/documentation` et se citent ici par leur nom de fichier.
 * Un écran qui change se recapture en relançant l'outil ; le test vérifie
 * qu'aucune capture citée ne manque, et qu'aucune n'est oubliée.
 */

/**
 * Un bloc de contenu : un paragraphe, une liste, des étapes numérotées, ou une
 * capture d'écran légendée.
 */
type Bloc =
  | string
  | { liste: string[] }
  | { etapes: string[] }
  | { capture: string; legende: string; etroite?: boolean };

const CAPTURES = import.meta.glob<string>('../assets/documentation/*.webp', {
  eager: true, import: 'default',
});

/** L'adresse d'une capture, d'après son nom de fichier sans extension. */
export function adresseCapture(nom: string): string | undefined {
  return CAPTURES[`../assets/documentation/${nom}.webp`];
}

/** Une capture d'écran : `etroite` pour une fenêtre, qui n'a pas à occuper toute la largeur. */
function capture(nom: string, legende: string, etroite = false): Bloc {
  return { capture: nom, legende, etroite };
}

interface Section {
  titre: string;
  blocs: Bloc[];
}

interface Question {
  question: string;
  reponse: string;
}

interface Module {
  /** Clé de l'icône dans `ICONES_MODULE`. */
  id: string;
  /** Adresse de la page : `/documentation/<cle>`. */
  cle: string;
  titre: string;
  chapeau: string;
  sections: Section[];
  questions?: Question[];
}

export const MODULES_DOC: Module[] = [
  // ── Bien démarrer ──────────────────────────────────────────────────────────
  {
    id: 'documentation',
    cle: 'bien-demarrer',
    titre: 'Bien démarrer',
    chapeau: 'Les premiers gestes, dans l’ordre.',
    sections: [
      {
        titre: 'Se connecter',
        blocs: [
          'Indiquez l’adresse électronique de votre compte et votre mot de passe, puis cliquez sur « Se connecter ».',
          capture('connexion', 'La page de connexion. « Mot de passe oublié ? » envoie un courriel pour en choisir un nouveau.'),
        ],
      },
      {
        titre: 'Vous avez votre propre espace',
        blocs: [
          {
            etapes: [
              'À la première connexion, la fenêtre « Ouvrons votre année scolaire » s’ouvre. Choisissez l’année scolaire et votre zone de vacances.',
              'Cliquez sur le ou les niveaux de votre classe, puis sur « Ouvrir l’année ». Les cinq périodes et les matières du programme se mettent en place toutes seules.',
              'Ajoutez vos élèves : menu « Évaluation », bouton « Gérer la classe ».',
              'Remplissez votre « Emploi du temps ». C’est lui qui crée les lignes du cahier journal.',
              'Ouvrez le « Cahier journal » : vos créneaux du jour y sont déjà.',
            ],
          },
        ],
      },
      {
        titre: 'Vous travaillez dans une école',
        blocs: [
          'La direction ouvre l’année scolaire pour toute l’école, crée les classes et vous confie la vôtre. Vous recevez un courriel pour choisir votre mot de passe.',
          {
            etapes: [
              'Connectez-vous avec l’adresse à laquelle vous avez reçu le courriel.',
              'Ajoutez vos élèves : menu « Évaluation », bouton « Gérer la classe ».',
              'Remplissez votre « Emploi du temps ».',
              'Ouvrez le « Cahier journal ».',
            ],
          },
          'Si une fenêtre vous indique que l’école n’a pas encore d’année scolaire, c’est à la direction de l’ouvrir.',
        ],
      },
      {
        titre: 'Se repérer dans le menu',
        blocs: [
          {
            liste: [
              'En haut : l’Accueil et les écrans de la classe, du cahier journal à l’évaluation.',
              '« Annexe » : l’Agenda et les 108 heures.',
              '« Administration » : les réglages, l’équipe de l’école, la sécurité de votre compte et l’abonnement.',
              'En bas : la documentation, le support, le choix du thème clair ou sombre, votre nom et « Se déconnecter ».',
            ],
          },
          '« Replier le menu » ne garde que les icônes, pour laisser plus de place aux tableaux.',
          'Dans une école, la direction voit aussi, en haut du menu, la liste « Classe » : elle permet d’ouvrir la classe d’un collègue.',
          capture('menu', 'Le menu, avec la liste « Classe » que voit la direction d’une école.', true),
        ],
      },
    ],
    questions: [
      {
        question: 'Faut-il enregistrer ce que j’écris ?',
        reponse: 'Presque jamais. Le cahier journal, les progressions, la programmation, les notes et l’agenda s’enregistrent pendant que vous écrivez. Seules les fenêtres qui s’ouvrent par-dessus l’écran ont un bouton « Enregistrer ».',
      },
      {
        question: 'Le texte est trop petit ou trop grand.',
        reponse: 'Utilisez les boutons − et + en bas à droite de l’écran. Cliquer sur le pourcentage revient à 100 %.',
      },
      {
        question: `Comment passer en thème ${LIBELLES_THEMES.dark.toLowerCase()} ?`,
        reponse: 'Choisissez-le dans la liste « Thème », en bas du menu.',
      },
    ],
  },

  // ── Accueil ────────────────────────────────────────────────────────────────
  {
    id: 'accueil',
    cle: 'accueil',
    titre: 'Accueil',
    chapeau: 'Votre journée d’un coup d’œil, et vos choses à faire.',
    sections: [
      {
        titre: 'Ce que montre l’accueil',
        blocs: [
          {
            liste: [
              '« Aujourd’hui » : les séances du jour, avec ce que vous avez préparé. « Ouvrir le cahier journal » vous emmène à cette journée.',
              '« À faire » : votre liste de choses à ne pas oublier.',
              '« Accès rapides » : les principaux écrans en un clic.',
            ],
          },
          capture('accueil', 'L’accueil d’un mardi : les séances du jour, le matériel à sortir, la liste « À faire » et les accès rapides.'),
          'Un bandeau jaune apparaît parfois tout en haut de l’écran. C’est un message de l’équipe du Journal de Classe, par exemple pour annoncer une intervention. Il disparaît tout seul quand il n’est plus utile.',
        ],
      },
      {
        titre: 'Comment ajouter une chose à faire',
        blocs: [
          {
            etapes: [
              'Écrivez dans le champ de la carte « À faire ».',
              'Cliquez sur « Ajouter », ou appuyez sur Entrée.',
              'Une fois la chose faite, cochez sa case.',
              'Le bouton « Vider les … faites » retire d’un coup toutes les lignes cochées.',
            ],
          },
          'Pour retirer une seule ligne, cliquez sur sa corbeille.',
        ],
      },
    ],
    questions: [
      {
        question: 'Qui voit ma liste « À faire » ?',
        reponse: 'Vous seul. Même la direction de l’école ne la voit pas.',
      },
    ],
  },

  // ── Cahier journal ─────────────────────────────────────────────────────────
  {
    id: 'journal',
    cle: 'cahier-journal',
    titre: 'Cahier journal',
    chapeau: 'Préparer chaque séance de la journée.',
    sections: [
      {
        titre: 'Se déplacer dans l’année',
        blocs: [
          {
            liste: [
              '« ← Préc. » et « Suiv. → » changent de semaine, en gardant le jour choisi. « Aujourd’hui » revient à la semaine en cours.',
              'Les onglets « Lundi » à « Vendredi » choisissent le jour.',
              'À gauche, cliquez sur une période pour voir ses semaines, puis sur une semaine pour l’ouvrir. Les semaines de vacances sont barrées.',
            ],
          },
          capture('journal', 'Le cahier journal d’un mardi : une ligne par créneau de l’emploi du temps, avec ses objectifs, son déroulement et son matériel.'),
        ],
      },
      {
        titre: 'Comment préparer une séance',
        blocs: [
          'Chaque ligne du tableau est un créneau de votre emploi du temps.',
          {
            etapes: [
              'Vérifiez le domaine et le sous-domaine. Ils reprennent ceux de l’emploi du temps.',
              'Ajoutez les objectifs : écrivez dans « Objectif libre… » puis appuyez sur Entrée, ou utilisez les boutons « Récupérer ».',
              'Remplissez le déroulement et le matériel.',
            ],
          },
          'Tout s’enregistre pendant que vous écrivez.',
          'Quand vous commencez à remplir une ligne vide, la séance prévue cette semaine dans votre progression pour cette matière est ajoutée comme premier objectif. Vous pouvez la modifier ou la supprimer.',
        ],
      },
      {
        titre: 'Comment récupérer un objectif',
        blocs: [
          {
            liste: [
              '« Récupérer depuis la progression » montre les séances prévues cette semaine pour le domaine de la ligne, détail par détail. S’il n’y en a pas, il propose celles des autres semaines.',
              '« Récupérer depuis le programme de l’année » montre le programme officiel du domaine de la ligne, avec une recherche. Indiquez d’abord un domaine sur la ligne.',
            ],
          },
          'Cliquez sur un élément : il est ajouté comme objectif, et la fenêtre se ferme. Le texte reste modifiable.',
          capture('journal-recuperer', '« Récupérer depuis la progression » : les détails de la séance prévue cette semaine, un par un.', true),
          capture('journal-programme', '« Récupérer depuis le programme de l’année » : le programme officiel du domaine, avec une recherche.', true),
        ],
      },
      {
        titre: 'Comment recopier une séance sur les semaines suivantes',
        blocs: [
          {
            etapes: [
              'Préparez la séance : au moins un objectif, un déroulement ou du matériel.',
              'Cochez la case de la colonne « Duplication ».',
            ],
          },
          'La séance est recopiée sur le même créneau de toutes les semaines suivantes. Une séance déjà remplie n’est jamais écrasée. Décocher retire les copies des semaines suivantes. Si vous en avez modifié certaines depuis, elles vous sont montrées : cochez celles à garder, les autres sont supprimées.',
        ],
      },
      {
        titre: 'Ajouter ou supprimer une ligne',
        blocs: [
          {
            liste: [
              '« Ajouter », en haut, ou « Ajouter une ligne », sous le tableau, ajoute une ligne vide en bas du tableau, pour une sortie ou un intervenant. Remplissez-la comme les autres : horaires, domaine, objectifs, déroulement, matériel. Elle s’enregistre dès la première saisie ; laissée vide, elle disparaît en changeant de jour.',
              'Quand la progression prévoit une séance pour la matière cette semaine, elle s’affiche en aperçu dans la case Objectifs : « Ajouter cette séance au journal » la recopie.',
              'La corbeille d’une ligne venant de l’emploi du temps supprime la séance ET le créneau : il ne reviendra plus dans le journal. Une confirmation est demandée.',
              'La corbeille d’un objectif ne supprime que cet objectif.',
            ],
          },
          '« Télécharger le PDF », en haut de l’écran, produit la journée telle qu’elle s’affiche, prête à imprimer.',
        ],
      },
    ],
    questions: [
      {
        question: 'Le tableau est vide.',
        reponse: 'Votre emploi du temps n’a pas de créneau ce jour-là. Remplissez-le, ou ajoutez une séance avec « Ajouter ».',
      },
      {
        question: 'Un créneau de l’emploi du temps n’apparaît pas.',
        reponse: 'S’il est rattaché à une période, il n’apparaît que pendant celle-ci.',
      },
      {
        question: '« Récupérer depuis le programme de l’année » ne trouve rien.',
        reponse: 'Vérifiez le domaine écrit sur la ligne, et le niveau de votre classe dans « Réglages », onglet « Général ».',
      },
    ],
  },

  // ── Fiche de préparation ───────────────────────────────────────────────────
  {
    id: 'fiches',
    cle: 'fiche-de-preparation',
    titre: 'Fiche de préparation',
    chapeau: 'Préparez vos séquences : leurs séances, et le déroulement de chaque séance phase par phase.',
    sections: [
      {
        titre: 'Comment elle est organisée',
        blocs: [
          'Une séquence contient des séances, chaque séance contient des phases. Vos séquences sont listées à gauche ; celle que vous ouvrez s’affiche à droite.',
          'Les séances se choisissent par onglets : « Séance 1 », « Séance 2 »… Une seule s’affiche à la fois.',
          capture('fiches', 'Une séquence de lecture ouverte sur sa première séance : objectifs, matériel, pièces jointes, puis les phases.'),
        ],
      },
      {
        titre: 'Créer une séquence',
        blocs: [
          {
            etapes: [
              'Cliquez sur « + Séquence ».',
              'Donnez-lui un titre, puis choisissez un domaine et, si vous le souhaitez, un sous-domaine. Les listes viennent du programme de votre classe.',
              'Écrivez le détail de la séquence et ses objectifs.',
              'Cliquez sur « Créer la séquence ».',
            ],
          },
          'Changer de domaine vide le sous-domaine : la liste des sous-domaines dépend du domaine choisi.',
        ],
      },
      {
        titre: 'Les objectifs',
        blocs: [
          {
            liste: [
              'Écrivez un objectif dans le champ, puis appuyez sur Entrée ou cliquez sur « Ajouter ».',
              '« Depuis le programme » ouvre les objectifs officiels du domaine, limités au sous-domaine s’il est choisi. Un champ permet de les filtrer.',
              'Un objectif repris du programme se modifie comme les autres : réécrivez-le à votre façon.',
              'La croix retire un objectif de la liste.',
            ],
          },
          'Sur une séance, les objectifs proposés sont ceux du domaine de sa séquence.',
        ],
      },
      {
        titre: 'Les séances',
        blocs: [
          '« + Séance » ajoute une séance à la séquence ouverte : un titre, ses objectifs, le matériel et un commentaire — différenciation, points de vigilance.',
          'Le crayon modifie la séance, la corbeille la supprime avec ses phases et ses pièces jointes.',
        ],
      },
      {
        titre: 'Les phases',
        blocs: [
          'Chaque phase porte un titre, une durée, un type (découverte, recherche, mise en commun…) et un dispositif (collectif, binôme, groupes…). La durée totale de la séance s’affiche à côté de son titre.',
          'La description se met en forme : gras, italique, souligné, barré, surlignage, couleur, police, taille et listes. Un texte collé depuis un autre document perd sa mise en forme d’origine.',
          capture('fiches-phase', 'La fenêtre d’une phase, avec la barre de mise en forme de la description.', true),
          'Les flèches montent ou descendent une phase dans la séance.',
        ],
      },
      {
        titre: 'Les pièces jointes',
        blocs: [
          '« Ajouter », dans le bloc « Pièces jointes » d’une séance, y joint un document : PDF, traitement de texte, tableur, présentation, image, son ou vidéo.',
          'Un fichier pèse 10 Mo au plus, et l’ensemble des pièces jointes de l’école 300 Mo.',
          'Cliquez sur le nom d’une pièce pour la télécharger.',
        ],
      },
      {
        titre: 'Télécharger en PDF',
        blocs: [
          '« PDF… » ouvre une fenêtre où vous cochez les séquences à mettre dans le PDF. Celle qui est ouverte est cochée d’avance.',
          'Chaque séquence commence sur une page neuve : une page de garde avec ses objectifs et son sommaire, puis une page par séance avec son déroulement. Toutes les séances y figurent, pas seulement celle qui est à l’écran.',
          capture('fiches-impression', 'Le choix des séquences à mettre dans le PDF.', true),
        ],
      },
    ],
    questions: [
      {
        question: 'La liste des domaines est vide.',
        reponse: 'Elle vient du programme de votre classe. Vérifiez le niveau de la classe dans « Réglages », onglet « Général ».',
      },
      {
        question: 'Une séquence apparaît-elle dans le cahier journal ?',
        reponse: 'Non. La fiche de préparation est indépendante : elle lit le programme, mais n’écrit ni dans le cahier journal ni dans les progressions.',
      },
      {
        question: 'Qui peut lire mes fiches ?',
        reponse: 'Les fiches appartiennent à la classe, comme le cahier journal. La direction de l’école, ou un remplaçant à qui la classe est confiée, peut les lire.',
      },
    ],
  },

  // ── Emploi du temps ────────────────────────────────────────────────────────
  {
    id: 'edt',
    cle: 'emploi-du-temps',
    titre: 'Emploi du temps',
    chapeau: 'Votre semaine type. À remplir en premier.',
    sections: [
      {
        titre: 'Comment ajouter un créneau',
        blocs: [
          {
            etapes: [
              'Cliquez dans la colonne du jour, à l’heure voulue. Vous pouvez aussi cliquer sur le « + » à côté du nom du jour, ou sur « Ajouter un créneau ».',
              'Choisissez la matière, et vérifiez l’heure de début et de fin.',
              'Si vous le souhaitez, choisissez un sous-domaine, un intitulé ou une couleur.',
              'Cliquez sur « Enregistrer ».',
            ],
          },
          'L’intitulé sert surtout aux créneaux sans matière : récréation, APC…',
          capture('edt', 'Une semaine type de CE2. Chaque créneau porte la couleur de sa matière.'),
          capture('edt-creneau', 'La fenêtre « Nouveau créneau ».', true),
        ],
      },
      {
        titre: 'Modifier, déplacer, supprimer',
        blocs: [
          {
            liste: [
              'Pour modifier ou supprimer un créneau, cliquez dessus.',
              'Pour le déplacer à un autre jour, faites-le glisser dans la colonne de ce jour. Son horaire ne change pas.',
            ],
          },
        ],
      },
      {
        titre: 'Comment recopier un créneau sur d’autres jours',
        blocs: [
          {
            etapes: [
              'Cliquez sur la petite icône de copie, en haut à droite du créneau.',
              'Cochez les jours voulus.',
              'Cliquez sur « Dupliquer ».',
            ],
          },
          'Un créneau déjà présent au même horaire n’est jamais écrasé.',
          capture('edt-dupliquer', 'La fenêtre « Dupliquer le créneau » : cochez les jours où le reproduire.', true),
        ],
      },
      {
        titre: 'Les périodes',
        blocs: [
          'Les boutons de période, en haut de l’écran, affichent les créneaux de chaque période. Un créneau vaut toute l’année, sauf si vous choisissez une période dans sa fenêtre. Il n’apparaît alors que pendant celle-ci : la piscine du deuxième trimestre, par exemple.',
          'Pour recopier la semaine d’une période dans une autre :',
          {
            etapes: [
              'Affichez la période à recopier.',
              'Cliquez sur « Dupliquer vers… ».',
              'Choisissez la période dans « Copier vers », puis cliquez sur « Copier ».',
            ],
          },
          'Attention : les créneaux déjà présents dans la période choisie sont remplacés. Leur nombre est annoncé avant de copier.',
        ],
      },
    ],
    questions: [
      {
        question: 'Un créneau est entouré de rouge.',
        reponse: 'Il chevauche un autre créneau. Ce n’est pas bloquant, mais la séance apparaîtrait deux fois dans le cahier journal.',
      },
      {
        question: 'Puis-je imprimer mon emploi du temps ?',
        reponse: 'Oui : « Télécharger le PDF », en haut de l’écran, produit un tableau sur une page, prêt à imprimer.',
      },
    ],
  },

  // ── Progressions ───────────────────────────────────────────────────────────
  {
    id: 'progressions',
    cle: 'progressions',
    titre: 'Progressions',
    chapeau: 'Prévoir une période entière, semaine par semaine.',
    sections: [
      {
        titre: 'Ce que montre l’écran',
        blocs: [
          'Une ligne par semaine de la période, une colonne par sous-domaine de la matière choisie. Les semaines de vacances sont marquées « Vacances ».',
          capture('progressions', 'Les progressions de mathématiques en période 2 : une séance par case, avec ses détails numérotés.'),
        ],
      },
      {
        titre: 'Comment prévoir une séance',
        blocs: [
          {
            etapes: [
              'Choisissez la période et la matière.',
              'Dans la case de la semaine et du sous-domaine, cliquez sur « Séance ».',
              'Cochez les notions proposées par votre programmation, ou écrivez une séance libre.',
              'Cliquez sur « Ajouter ». Chaque notion cochée devient une séance.',
            ],
          },
          'Le texte d’une séance se modifie directement dans la case. Le « + » sous une séance lui ajoute un détail.',
          capture('progressions-seance', 'La fenêtre « Séance » : les notions de votre programmation, à cocher, ou une séance libre.', true),
        ],
      },
      {
        titre: 'Comment reporter une séance',
        blocs: [
          'Cliquez sur la croix rouge d’une séance : elle passe à la semaine suivante, avec l’étiquette « Reporté ». La croix d’un détail ne reporte que ce détail. Cliquez à nouveau pour annuler.',
        ],
      },
      {
        titre: 'Comment récupérer ce qui n’a pas été fait',
        blocs: [
          {
            etapes: [
              'Cliquez sur « Récupération ».',
              'Cliquez sur « Récupérer ».',
            ],
          },
          'Les séances non traitées des périodes précédentes sont recopiées sur la semaine en cours. Les séances d’origine restent à leur place.',
          capture('progressions-recuperation', 'La fenêtre « Récupération », avant de confirmer.', true),
        ],
      },
    ],
    questions: [
      {
        question: 'Rien ne m’est proposé quand je clique sur « Séance ».',
        reponse: 'Votre programmation est vide pour cette période et cette matière. Remplissez-la, ou écrivez une séance libre.',
      },
    ],
  },

  // ── Programmation ──────────────────────────────────────────────────────────
  {
    id: 'programmation',
    cle: 'programmation',
    titre: 'Programmation',
    chapeau: 'Ce que vous verrez dans l’année, période par période.',
    sections: [
      {
        titre: 'La différence avec les progressions',
        blocs: [
          'La programmation dit ce que vous verrez dans l’année. Les progressions disent quelle semaine. Ce que vous programmez ici vous est ensuite proposé dans les progressions.',
        ],
      },
      {
        titre: 'Comment remplir la programmation',
        blocs: [
          'Une ligne par sous-domaine, une colonne par période.',
          {
            etapes: [
              'Choisissez la matière.',
              'Dans la case du sous-domaine et de la période, cliquez sur « Libre » pour écrire vous-même, ou sur « Programme » pour choisir dans le programme officiel.',
              'Avec « Programme », cherchez une notion, cliquez dessus pour l’ajouter, puis fermez la fenêtre.',
            ],
          },
          'Le texte se modifie directement dans la case, et s’enregistre tout seul.',
          capture('programmation', 'La programmation de mathématiques : une ligne par sous-domaine, une colonne par période.'),
          capture('programmation-programme', 'La fenêtre « Programme » : cherchez une notion du programme officiel, puis cliquez dessus.', true),
        ],
      },
    ],
    questions: [
      {
        question: 'L’écran indique « Aucun domaine dans le programme ».',
        reponse: 'Le niveau de votre classe n’est pas réglé. Indiquez-le dans « Réglages », onglet « Général », ou demandez-le à la direction.',
      },
    ],
  },

  // ── Évaluation ─────────────────────────────────────────────────────────────
  {
    id: 'evaluation',
    cle: 'evaluation',
    titre: 'Évaluation',
    chapeau: 'Vos élèves, vos contrôles, et leurs acquis.',
    sections: [
      {
        titre: 'Comment ajouter vos élèves',
        blocs: [
          {
            etapes: [
              'Cliquez sur « Gérer la classe ».',
              'Cliquez sur « Ajouter un élève », indiquez son nom et son prénom, puis « Enregistrer ».',
            ],
          },
          'Pour ajouter toute la classe d’un coup :',
          {
            etapes: [
              'Cliquez sur « Importer ».',
              'Collez la liste, un élève par ligne : « Dupont Marie » ou « Dupont, Marie ».',
              'Vérifiez le nombre d’élèves annoncé, puis cliquez sur « Importer ».',
            ],
          },
          'N’écrivez que le nom et le prénom. Jamais d’information de santé ou de situation familiale, même entre parenthèses.',
          capture('evaluation-classe', 'La fenêtre « Gérer la classe » : le crayon modifie un élève, la corbeille le retire.', true),
        ],
      },
      {
        titre: 'Comment noter un contrôle',
        blocs: [
          {
            etapes: [
              'Choisissez la matière.',
              'Sous le domaine voulu, cliquez sur « Ajouter un contrôle ».',
              'Donnez-lui un intitulé, par exemple « Dictée de mots », puis « Enregistrer ».',
              'Cliquez dans la case d’un élève : le premier clic pose « Dépassé », le suivant « Atteint », puis « Partiellement atteint », « Non atteint », et enfin plus rien. Les touches 0 à 4 posent un niveau directement.',
            ],
          },
          'Les quatre niveaux sont ceux du livret scolaire :',
          {
            liste: [
              'NA : non atteint',
              'PA : partiellement atteint',
              'A : atteint',
              'D : dépassé',
            ],
          },
          'Au clavier, les touches 1 à 4 posent directement un niveau, et 0 efface la case.',
          capture('evaluation', 'Les contrôles de lecture : une colonne par contrôle, et la moyenne de chaque élève dans le domaine.'),
        ],
      },
      {
        titre: 'Les autres gestes',
        blocs: [
          {
            liste: [
              'Pour renommer ou supprimer un contrôle, cliquez sur son titre.',
              'La colonne « Moyenne » donne le niveau moyen de l’élève dans le domaine.',
              '« Statistiques » montre toute la classe, domaine par domaine.',
              '« Télécharger le PDF » produit le tableau affiché, prêt à imprimer.',
            ],
          },
          capture('evaluation-statistiques', '« Statistiques » : le niveau de chaque élève, domaine par domaine. Le nombre de contrôles est entre parenthèses.'),
        ],
      },
    ],
    questions: [
      {
        question: 'Pourquoi pas de note sur 20 ?',
        reponse: 'L’évaluation se fait par compétences, comme dans le livret scolaire. La moyenne s’exprime donc en niveau, jamais en chiffre.',
      },
      {
        question: 'Supprimer un contrôle efface-t-il les notes ?',
        reponse: 'Oui. Les contrôles suivants sont renumérotés.',
      },
    ],
  },

  // ── Agenda ─────────────────────────────────────────────────────────────────
  {
    id: 'agenda',
    cle: 'agenda',
    titre: 'Agenda',
    chapeau: 'Une page par jour, où vous écrivez librement.',
    sections: [
      {
        titre: 'À quoi il sert',
        blocs: [
          'À noter ce qui n’entre pas dans le cahier journal : un mot à dire à un parent, une idée pour demain, ce qui s’est passé à la récréation.',
          'Il s’ouvre sur la prochaine journée de classe. Le mercredi et le week-end sont sautés.',
          capture('agenda', 'L’agenda ouvert un mardi : il montre le jeudi, prochain jour de classe. Les points du calendrier signalent les pages écrites.'),
        ],
      },
      {
        titre: 'Comment écrire',
        blocs: [
          {
            etapes: [
              'Ouvrez « Agenda », dans la partie « Annexe » du menu.',
              'Cliquez dans la page et écrivez.',
            ],
          },
          'Le texte s’enregistre tout seul, une seconde après votre dernière frappe. Il s’écrit en Marelle, l’écriture scolaire du ministère de l’Éducation nationale.',
        ],
      },
      {
        titre: 'Changer de jour',
        blocs: [
          {
            liste: [
              'Cliquez sur un jour du petit calendrier. Un point sous un jour signale une page déjà écrite.',
              'Utilisez les flèches ← et → en haut de la page, ou celles du clavier quand vous n’êtes pas en train d’écrire.',
              '« Prochain jour de classe » et « Aujourd’hui » vous ramènent aux jours utiles.',
            ],
          },
        ],
      },
      {
        titre: 'Mettre en forme',
        blocs: [
          {
            liste: [
              '« A− » et « A+ » changent la taille du texte. Les lignes de la page suivent.',
              'Sélectionnez du texte, puis choisissez une couleur ou un surlignage.',
              'Le bouton ✕ enlève la couleur et le surlignage du texte sélectionné.',
            ],
          },
          'Un texte collé depuis un autre document perd sa mise en forme d’origine.',
        ],
      },
      {
        titre: 'Le plein écran',
        blocs: [
          'Le bouton en haut à droite de la page affiche l’agenda sur tout l’écran. Cliquez à nouveau dessus, ou appuyez sur Échap, pour revenir.',
        ],
      },
    ],
    questions: [
      {
        question: 'Qui peut lire mon agenda ?',
        reponse: 'L’agenda appartient à la classe, comme le cahier journal. La direction de l’école, ou un remplaçant à qui la classe est confiée, peut le lire.',
      },
      {
        question: 'Puis-je imprimer l’agenda ?',
        reponse: 'Non, l’agenda se consulte à l’écran.',
      },
    ],
  },

  // ── 108 heures ─────────────────────────────────────────────────────────────
  {
    id: 'heures',
    cle: '108-heures',
    titre: '108 heures',
    chapeau: 'Le suivi de vos heures de service, en plus des heures de classe.',
    sections: [
      {
        titre: 'Comment noter une activité',
        blocs: [
          {
            etapes: [
              'Cliquez sur le jour de l’activité dans le calendrier.',
              'Indiquez la durée, la catégorie et un intitulé, par exemple « APC lecture ».',
              'Si vous le souhaitez, précisez la modalité et une description.',
              'Cliquez sur « Enregistrer ».',
            ],
          },
          'Pour modifier ou supprimer une activité, cliquez dessus.',
          capture('heures', 'Le calendrier des activités et, à droite, le récapitulatif annuel, catégorie par catégorie.'),
        ],
      },
      {
        titre: 'Comment noter une activité qui se répète',
        blocs: [
          {
            etapes: [
              'Cliquez sur « Activité récurrente ».',
              'Choisissez la catégorie, la durée, les dates de début et de fin, et les jours de la semaine.',
              'Laissez cochée « Ne rien créer pendant les vacances ».',
              'Cliquez sur « Créer la série ».',
            ],
          },
          'Pour retirer toutes les dates d’un coup, ouvrez l’une d’elles et cliquez sur « Supprimer toute la série ».',
          capture('heures-recurrente', 'La fenêtre « Activité récurrente ».', true),
        ],
      },
      {
        titre: 'Suivre vos heures',
        blocs: [
          'Le récapitulatif annuel compare ce que vous avez fait aux objectifs. Par défaut :',
          {
            liste: [
              'APC : 36 h',
              'Rendez-vous avec les parents, conseils des maîtres et de cycles : 48 h',
              'Formation continue et animation pédagogique, ensemble : 18 h',
              'Conseil d’école : 6 h',
              'Total : 108 h',
            ],
          },
          'Le bouton « Objectifs » permet de modifier ces valeurs.',
        ],
      },
    ],
    questions: [
      {
        question: 'Mes collègues voient-ils mes heures ?',
        reponse: 'Non. Le décompte est personnel : chaque enseignant a le sien.',
      },
      {
        question: 'À quoi sert la catégorie « Autre » ?',
        reponse: 'À noter une heure qui n’entre dans aucune autre catégorie. Elle compte dans le total, sans objectif propre.',
      },
      {
        question: 'Puis-je voir une seule semaine, ou une seule catégorie ?',
        reponse: 'Oui : choisissez « Semaine » au lieu de « Mois », et une catégorie dans la liste.',
      },
    ],
  },

  // ── Impressions ────────────────────────────────────────────────────────────
  {
    id: 'impressions',
    cle: 'impressions',
    titre: 'Impressions',
    chapeau: 'Le dossier de la classe en un seul document : les modules que vous choisissez, sur l’année ou une période.',
    sections: [
      {
        titre: 'À quoi il sert',
        blocs: [
          'Chaque écran produit le PDF de ce qu’il affiche : une journée du cahier journal, une matière sur une période. « Impressions » rassemble tout dans un dossier : pour une inspection, un remplacement, ou pour archiver l’année.',
          capture('impressions', 'Les paramètres du dossier : l’année, la classe, la période, puis les modules à imprimer.'),
        ],
      },
      {
        titre: 'Préparer un dossier',
        blocs: [
          {
            etapes: [
              'Ouvrez « Impressions », dans la partie « Annexe » du menu.',
              'Choisissez l’année scolaire, la classe et la période — ou « Toute l’année ».',
              'Cochez les modules à imprimer. Pour les fiches de préparation, cochez les séquences ; pour la programmation, les progressions et l’évaluation, les matières.',
              'Cliquez sur « Télécharger le PDF ».',
            ],
          },
          'Le dossier arrive en PDF dans vos téléchargements, prêt à imprimer, à archiver ou à transmettre.',
        ],
      },
      {
        titre: 'Ce que contient le dossier',
        blocs: [
          {
            liste: [
              'Une page de garde : l’école, la classe, l’année, la période et le sommaire.',
              'La liste des élèves et l’emploi du temps.',
              'La programmation annuelle, sur toutes les périodes de l’année.',
              'Les progressions, semaine par semaine.',
              'Le cahier journal : chaque journée où une séance est consignée, une par page si vous le souhaitez.',
              'Les fiches de préparation, avec leur page de garde et toutes leurs séances.',
              'Les grilles d’évaluation, avec la synthèse par domaine.',
              'Le bilan des 108 heures et la liste des activités.',
            ],
          },
          'Chaque module commence sur une page neuve. Les grands tableaux — programmation, progressions, cahier journal, évaluation — sont en paysage.',
        ],
      },
    ],
    questions: [
      {
        question: 'Puis-je imprimer une année passée ?',
        reponse: 'Oui : choisissez l’année, puis la classe de cette année-là. Votre classe de travail ne change pas.',
      },
      {
        question: 'Le téléchargement est long.',
        reponse: 'Une année entière de cahier journal compte une quarantaine de semaines : la préparation prend quelques secondes. L’écran indique où il en est.',
      },
    ],
  },

  // ── Réglages ───────────────────────────────────────────────────────────────
  {
    id: 'reglages',
    cle: 'reglages',
    titre: 'Réglages',
    chapeau: 'L’école, la classe, l’année scolaire et les matières.',
    sections: [
      {
        titre: 'Ce que contient chaque onglet',
        blocs: [
          {
            liste: [
              '« Général » : le nom de l’école, le niveau de la classe et la zone de vacances.',
              '« Année scolaire » : vos années, et celle qui est en cours.',
              '« Périodes » : les dates des cinq périodes, d’après le calendrier officiel.',
              '« Matières & domaines » : la couleur de chaque matière.',
              '« Suppressions » : récupérer une copie de vos données, ou tout effacer.',
            ],
          },
          capture('reglages-general', 'L’onglet « Général » : le nom de l’école, le niveau de la classe et la zone de vacances, avec ses dates.'),
          capture('reglages-periodes', 'L’onglet « Périodes » : les cinq périodes de l’année, d’après le calendrier officiel.'),
        ],
      },
      {
        titre: 'Comment préparer l’année suivante',
        blocs: [
          {
            etapes: [
              'Onglet « Année scolaire », cliquez sur « Nouvelle année ». Dans une école, c’est la direction qui ouvre l’année, pour tous les enseignants.',
              'Choisissez la rentrée dans la liste. Ses dates s’affichent : elles viennent du calendrier officiel, rien n’est à saisir.',
              'Cliquez sur « Créer ».',
              'Une fenêtre propose de reprendre l’année précédente : tous les enseignants et tous les modules sont cochés. Décochez ce qui ne doit pas suivre, puis cliquez sur « Voir ce qui sera repris » pour vérifier, et sur « Reprendre ».',
              'Le moment venu, cliquez sur « Activer » à côté de la nouvelle année.',
            ],
          },
          'Créer une année ne l’active pas : vous continuez à travailler sur l’année en cours tant que vous n’avez pas basculé.',
          'La reprise recrée chaque classe avec son contenu, recalé sur le calendrier de la nouvelle année : une séquence de la 3ᵉ semaine de la période 2 va dans la 3ᵉ semaine de la période 2. Les séances et séquences sont à refaire, les contrôles sont repris sans les résultats. Rien de ce qui existe déjà n’est remplacé. Vous pouvez aussi reprendre plus tard, avec « Reprendre… » à côté de l’année.',
          'Le crayon d’une année la remet sur les dates du calendrier officiel, par exemple après une correction publiée en cours d’année.',
          capture('reglages-annee', 'L’onglet « Année scolaire » : l’année en cours, et le bouton « Nouvelle année ».'),
        ],
      },
      {
        titre: 'Comment changer la couleur d’une matière',
        blocs: [
          'Onglet « Matières & domaines », cliquez sur la pastille de couleur de la matière. Le texte reste lisible quelle que soit la couleur choisie.',
          capture('reglages-matieres', 'L’onglet « Matières & domaines » : chaque matière du programme, ses domaines et sa pastille de couleur.'),
        ],
      },
      {
        titre: 'Comment exporter vos données',
        blocs: [
          {
            etapes: [
              'Onglet « Suppressions », cliquez sur « Exporter mes données » — « Exporter les données de l’école » pour la direction.',
              'Ouvrez le fichier reçu avec Excel ou LibreOffice.',
            ],
          },
          'La carte « Supprimer l’espace », juste en dessous, efface toutes les données de l’école. Exportez-les d’abord si vous souhaitez en garder une copie.',
          capture('reglages-suppressions', 'L’onglet « Suppressions », vu par la direction d’une école.'),
        ],
      },
    ],
    questions: [
      {
        question: 'Je ne peux pas modifier les périodes.',
        reponse: 'C’est normal : elles suivent le calendrier officiel de votre zone. Si une date vous semble fausse, écrivez au support.',
      },
      {
        question: 'Le niveau ou la zone sont grisés.',
        reponse: 'Dans une école, c’est la direction qui les règle.',
      },
      {
        question: 'Comment récupérer une copie de mes données ?',
        reponse: 'Onglet « Suppressions », bouton « Exporter mes données » : un fichier qui s’ouvre dans Excel ou LibreOffice, avec les données de tous les modules. Un enseignant exporte ses propres données ; la direction exporte celles de toute l’école en un clic, avec « Exporter les données de l’école ».',
      },
      {
        question: 'Que se passe-t-il si je supprime l’espace ?',
        reponse: 'L’espace est fermé tout de suite et l’abonnement est résilié au terme de la période déjà payée. Pendant 30 jours, vous pouvez le réactiver en vous reconnectant, si l’abonnement est réglé. Passé ce délai, l’effacement est définitif. Seul le propriétaire de l’espace peut le supprimer.',
      },
      {
        question: 'Où se trouve le bouton de suppression ?',
        reponse: 'Onglet « Suppressions », carte « Supprimer l’espace ». Une confirmation est demandée.',
      },
    ],
  },

  // ── Direction ──────────────────────────────────────────────────────────────
  {
    id: 'equipe',
    cle: 'direction',
    titre: 'Direction d’école',
    chapeau: 'Ouvrir l’année, créer les classes et accompagner l’équipe.',
    sections: [
      {
        titre: 'Comment ouvrir l’année de l’école',
        blocs: [
          {
            etapes: [
              'À la première connexion, la fenêtre « Ouvrons votre année scolaire » s’ouvre.',
              'Choisissez l’année scolaire et la zone de vacances, puis cliquez sur « Ouvrir l’année ».',
            ],
          },
          'L’année, ses cinq périodes et les matières valent pour toute l’école. Personne n’aura à les refaire.',
          capture('premiere-connexion', 'La fenêtre « Ouvrons votre année scolaire », à la première connexion de la direction.', true),
        ],
      },
      {
        titre: 'Comment créer les classes',
        blocs: [
          {
            etapes: [
              'Ouvrez « Équipe », dans la partie « Administration » du menu.',
              'Dans la carte « Classes », indiquez le nom de la classe, par exemple « CE2 A ».',
              'Cliquez sur ses niveaux.',
              'Choisissez son titulaire si son compte existe déjà, puis cliquez sur « Créer la classe ».',
            ],
          },
          'Le niveau est important : c’est lui qui détermine le programme proposé à la classe.',
        ],
      },
      {
        titre: 'Comment ajouter un collègue',
        blocs: [
          {
            etapes: [
              'Dans la carte « Ajouter un collègue », choisissez « Créer le compte » ou « Inviter ».',
              'Indiquez son prénom, son nom, son adresse électronique et son rôle.',
              'Cliquez sur « Créer le compte » ou « Envoyer l’invitation ».',
            ],
          },
          {
            liste: [
              '« Créer le compte » : le compte existe tout de suite. Vous pouvez lui confier sa classe avant même qu’il se connecte, et il reçoit un courriel pour choisir son mot de passe.',
              '« Inviter » : pour une adresse qui a déjà un compte ailleurs. Le collègue accepte l’invitation depuis le courriel reçu.',
            ],
          },
          'Les invitations pas encore acceptées apparaissent dans « Invitations en attente », où vous pouvez les annuler.',
        ],
      },
      {
        titre: 'Confier une classe, gérer l’équipe',
        blocs: [
          {
            liste: [
              'Pour confier une classe, choisissez son titulaire dans la carte « Classes ».',
              'Pour corriger le nom d’un collègue, cliquez sur le crayon de sa ligne.',
              'Pour changer son rôle, choisissez-le dans la colonne « Rôle » : Propriétaire, Administrateur ou Enseignant.',
              'La corbeille de sa ligne lui retire l’accès à l’école. Son compte et son travail ne sont pas supprimés.',
            ],
          },
          'Pour corriger votre propre nom, cliquez sur votre nom en bas du menu.',
          capture('equipe', 'L’écran « Équipe » : les membres et leur rôle, les classes et leur titulaire, puis l’ajout d’un collègue.'),
        ],
      },
      {
        titre: 'Comment ouvrir la classe d’un collègue',
        blocs: [
          {
            etapes: [
              'Dans le menu, choisissez la classe dans la liste « Classe ».',
              'L’écran se recharge : le cahier journal, les fiches de préparation, l’emploi du temps, les progressions, l’évaluation et l’agenda sont ceux de cette classe.',
              'Si vous avez vous-même une classe, choisissez « Ma classe » dans la même liste pour y revenir.',
            ],
          },
          'Vous pouvez tout consulter et tout modifier, comme le titulaire.',
        ],
      },
    ],
    questions: [
      {
        question: 'Je ne vois pas « Équipe » dans le menu.',
        reponse: 'La gestion d’une équipe fait partie de l’offre École. Votre offre actuelle ne couvre qu’un seul compte.',
      },
      {
        question: 'Un enseignant peut-il corriger son nom lui-même ?',
        reponse: 'Non, c’est vous qui le faites depuis « Équipe ».',
      },
      {
        question: 'Qui écrit au support ?',
        reponse: 'La personne qui administre l’espace de l’école. Les enseignants vous signalent ce qui les bloque.',
      },
    ],
  },

  // ── Abonnement ─────────────────────────────────────────────────────────────
  {
    id: 'abonnement',
    cle: 'abonnement',
    titre: 'Abonnement',
    chapeau: 'Votre offre, votre paiement et vos factures.',
    sections: [
      {
        titre: 'Ce que montre l’écran',
        blocs: [
          {
            liste: [
              '« Votre offre » : la formule, et la date du prochain renouvellement.',
              '« Consommation » : ce que votre offre permet, et ce que vous utilisez.',
              '« Paiement et factures » : vos moyens de paiement et vos factures.',
            ],
          },
          capture('abonnement', 'L’écran « Abonnement » d’une école : l’offre, son renouvellement et les comptes utilisés.'),
        ],
      },
      {
        titre: 'Comment changer de carte, d’offre, ou résilier',
        blocs: [
          {
            etapes: [
              'Cliquez sur « Gérer le paiement ».',
              'Faites la modification dans l’espace de paiement qui s’ouvre.',
            ],
          },
          'Ne souscrivez pas une seconde fois pour changer d’offre : vous paieriez deux abonnements.',
        ],
      },
    ],
    questions: [
      {
        question: 'Que se passe-t-il si je résilie ?',
        reponse: 'Vous gardez l’accès complet jusqu’à la fin de la période déjà payée.',
      },
      {
        question: 'Nous avons réduit le nombre de sièges : que se passe-t-il ?',
        reponse: 'Si l’école a plus de comptes que de sièges payés, personne n’est coupé tout de suite : un bandeau et un courriel préviennent la direction. Jusqu’au renouvellement de l’abonnement, retirez les comptes en trop dans « Équipe », ou reprenez des sièges dans « Abonnement ». Passé cette date, les comptes les plus récents passent en lecture seule : leurs données restent consultables, mais ne peuvent plus être modifiées. L’écran Équipe indique quels comptes sont concernés.',
      },
      {
        question: 'Où trouver mes factures ?',
        reponse: 'Dans « Abonnement », carte « Factures et avoirs » : chaque facture et chaque avoir s’y télécharge. Un avoir est le document qui accompagne un remboursement ou une réduction : il indique la facture qu’il corrige, et si la somme vous a été rendue ou sera déduite de vos prochaines factures.',
      },
      {
        question: 'Et si un paiement est refusé ?',
        reponse: 'Rien n’est coupé au premier refus : la banque est relancée automatiquement pendant plusieurs jours, et vous travaillez normalement. Mettez votre carte à jour depuis « Abonnement ». Si le paiement ne passe toujours pas, seules la facturation et la copie de vos données restent accessibles, vos données sont intactes et tout revient dès le paiement enregistré. Dans une école, prévenez la personne qui administre le compte.',
      },
      {
        question: 'Mes données sont-elles gardées après la fin de l’abonnement ?',
        reponse: 'Oui, pendant 90 jours, avec des rappels avant toute suppression.',
      },
    ],
  },

  // ── Sécurité ───────────────────────────────────────────────────────────────
  {
    id: 'securite',
    cle: 'securite',
    titre: 'Sécurité',
    chapeau: 'Protéger votre compte avec un code de connexion.',
    sections: [
      {
        titre: 'Pourquoi protéger votre compte',
        blocs: [
          'Votre compte donne accès aux noms de vos élèves. Avec la double authentification, un code à six chiffres est demandé à chaque connexion, en plus du mot de passe. Ce code s’affiche dans une application installée sur votre téléphone.',
        ],
      },
      {
        titre: 'Comment l’activer',
        blocs: [
          {
            etapes: [
              'Installez sur votre téléphone une application de codes, par exemple Google Authenticator ou Microsoft Authenticator.',
              'Dans « Sécurité », carte « Double authentification », cliquez sur « Activer ».',
              'Dans l’application du téléphone, ajoutez un compte et scannez le QR code affiché à l’écran. Sans appareil photo, ouvrez « Saisir la clé à la main » et recopiez-la.',
              'Tapez le code à six chiffres affiché par l’application, puis cliquez sur « Confirmer l’activation ».',
              'Copiez vos codes de secours, rangez-les en lieu sûr, cochez la case de confirmation et cliquez sur « J’ai terminé ».',
            ],
          },
          capture('securite', 'La carte « Double authentification », avant l’activation.'),
          capture('securite-activation', 'Après « Activer » : le QR code à scanner, puis le code à six chiffres à saisir.'),
        ],
      },
    ],
    questions: [
      {
        question: 'J’ai perdu mon téléphone.',
        reponse: 'Connectez-vous avec l’un de vos codes de secours. Chaque code ne sert qu’une fois. Si vous n’avez plus vos codes non plus, écrivez-nous depuis la page « Nous contacter » : après vérification de votre identité, nous retirons la double authentification de votre compte, et vous la réactivez avec votre nouveau téléphone.',
      },
      {
        question: 'Où garder mes codes de secours ?',
        reponse: 'Ailleurs que sur votre téléphone : imprimés, ou dans un carnet rangé chez vous.',
      },
      {
        question: 'J’ai oublié mon mot de passe.',
        reponse: 'Sur la page de connexion, cliquez sur « Mot de passe oublié ? ». Vous recevrez un courriel pour en choisir un nouveau.',
      },
    ],
  },

  // ── Support ────────────────────────────────────────────────────────────────
  {
    id: 'support',
    cle: 'support',
    titre: 'Support',
    chapeau: 'Poser une question, signaler un problème.',
    sections: [
      {
        titre: 'Comment écrire au support',
        blocs: [
          {
            etapes: [
              'Ouvrez « Support », en bas du menu, puis cliquez sur « Nouvelle demande ».',
              'Choisissez de quoi il s’agit.',
              'Donnez un sujet court, comme le titre d’un courriel.',
              'Décrivez ce que vous faisiez et ce qui s’est passé, puis cliquez sur « Envoyer ».',
            ],
          },
          'N’écrivez jamais le nom d’un élève : décrivez la situation.',
          capture('support-nouvelle', 'La fenêtre « Nouvelle demande ».', true),
        ],
      },
      {
        titre: 'Suivre une demande',
        blocs: [
          'Vos demandes s’affichent dans « Vos demandes », avec leur état :',
          {
            liste: [
              '« En attente de réponse » : le support ne vous a pas encore répondu.',
              '« Réponse reçue » : ouvrez la demande pour lire la réponse, et répondre si besoin.',
              '« Clos » : la demande est terminée. Pour une suite, ouvrez une nouvelle demande.',
              '« Annulé » : vous avez annulé la demande.',
            ],
          },
          'Tant qu’elle est en cours, « Annuler la demande » la retire si vous avez trouvé la solution.',
          capture('support', 'Vos demandes au support, avec leur état.'),
        ],
      },
    ],
    questions: [
      {
        question: 'Je ne vois pas « Support » dans le menu.',
        reponse: 'Dans une école, c’est la personne qui administre l’espace qui écrit au support. Signalez-lui ce qui vous bloque.',
      },
      {
        question: 'Sous quel délai aurai-je une réponse ?',
        reponse: 'Il n’y a pas de délai garanti. Les problèmes de fonctionnement, la facturation et les demandes liées aux données personnelles sont traités en priorité.',
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════

/**
 * La documentation, consultable SANS compte.
 *
 * Avant de s'inscrire, on veut savoir à quoi ressemble l'outil et comment il
 * s'utilise : c'est précisément ce que la documentation montre, captures
 * comprises. Elle ne contient rien de propre à un client — l'école des
 * captures est fictive — et peut donc être publique, aux MÊMES adresses : un
 * lien envoyé à un collègue fonctionne qu'il soit connecté ou non.
 *
 * Habillage des pages légales, et non la coquille : sans compte, il n'y a ni
 * menu ni classe à afficher.
 */
export function DocumentationPublique() {
  return (
    <div className="min-h-dvh bg-[var(--color-bg)]">
      <main id="contenu" className="mx-auto max-w-[64rem] px-5 py-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/offres"
            className="text-[14px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
          >
            ← Journal de Classe
          </Link>
          <span className="flex flex-wrap items-center gap-2">
            <LienBouton vers="/" variante="ghost" taille="sm">Se connecter</LienBouton>
            <LienBouton vers="/inscription" variante="primary" taille="sm">Créer un espace</LienBouton>
          </span>
        </div>
        <Documentation publique />
        <PiedDePage />
      </main>
    </div>
  );
}

/**
 * Où mènent les « écrivez-nous » : au support une fois connecté, au
 * formulaire de contact sinon — le support exige un compte.
 */
function LienAide({ publique, children }: { publique: boolean; children: string }) {
  return (
    <Link to={publique ? '/contact' : '/support'} className="underline">
      {publique ? 'Écrivez-nous.' : children}
    </Link>
  );
}

export function Documentation({ publique = false }: { publique?: boolean }) {
  const { module } = useParams();
  const actif = MODULES_DOC.find((m) => m.cle === module);

  if (actif) return <PageModule module={actif} publique={publique} />;

  return (
    <div className="flex flex-col gap-5">
      <EnTetePage
        titre="Documentation"
        contexte="Comment faire, écran par écran."
      />

      <Message ton="info" titre="Première visite ?">
        Commencez par la page{' '}
        <Link to="/documentation/bien-demarrer" className="underline">Bien démarrer</Link>
        {' '}: elle donne les premiers gestes, dans l’ordre.
      </Message>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MODULES_DOC.map((m) => {
          const Icone = ICONES_MODULE[m.id];
          return (
            <Link
              key={m.cle}
              to={`/documentation/${m.cle}`}
              className="flex flex-col gap-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 no-underline shadow-[var(--ombre-carte)] transition-colors hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
            >
              <span className="flex items-center gap-2 font-semibold text-[var(--color-fg)]">
                {Icone && <Icone taille={17} />}
                {m.titre}
              </span>
              <span className="text-[13px] leading-snug text-[var(--color-fg-muted)]">
                {m.chapeau}
              </span>
            </Link>
          );
        })}
      </div>

      <Message ton="info">
        Une question à laquelle rien ne répond ici ?{' '}
        <LienAide publique={publique}>Ouvrez une demande au support.</LienAide>
      </Message>
    </div>
  );
}

/** Un bloc rendu : paragraphe, liste à puces, étapes numérotées, ou capture. */
function RenduBloc({ bloc }: { bloc: Bloc }) {
  if (typeof bloc === 'string') return <p className="max-w-[70ch]">{bloc}</p>;
  if ('capture' in bloc) {
    // Une capture absente ne casse pas la page : le texte suffit à suivre
    // les étapes. Le test, lui, refuse qu'il en manque une.
    const source = adresseCapture(bloc.capture);
    if (!source) return null;
    return (
      <Illustration
        source={source}
        legende={bloc.legende}
        largeur={bloc.etroite ? 'etroite' : 'pleine'}
      />
    );
  }
  if ('etapes' in bloc) {
    return (
      <ol className="ml-5 max-w-[70ch] list-decimal space-y-1.5 marker:font-semibold marker:text-[var(--color-primary)]">
        {bloc.etapes.map((e) => <li key={e} className="pl-1">{e}</li>)}
      </ol>
    );
  }
  return (
    <ul className="ml-5 max-w-[70ch] list-disc space-y-1">
      {bloc.liste.map((l) => <li key={l}>{l}</li>)}
    </ul>
  );
}

function PageModule({ module, publique }: { module: Module; publique: boolean }) {
  const Icone = ICONES_MODULE[module.id];
  return (
    <div className="flex flex-col gap-5">
      <EnTetePage
        titre={module.titre}
        contexte={module.chapeau}
        actions={
          // Un lien et non un bouton : c'est une navigation, et le retour
          // arrière comme l'ouverture dans un onglet doivent marcher.
          <LienBouton vers="/documentation" variante="secondary" taille="sm">
            Toute la documentation
          </LienBouton>
        }
      />

      <div className="flex flex-col gap-4">
        {module.sections.map((s) => (
          <Carte key={s.titre} titre={s.titre}>
            <div className="flex flex-col gap-3 text-[14px] leading-relaxed">
              {s.blocs.map((b, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <RenduBloc key={i} bloc={b} />
              ))}
            </div>
          </Carte>
        ))}

        {module.questions && module.questions.length > 0 && (
          <Carte titre="Questions fréquentes">
            <dl className="flex max-w-[70ch] flex-col gap-4 text-[14px] leading-relaxed">
              {module.questions.map((q) => (
                <div key={q.question}>
                  <dt className="font-semibold">{q.question}</dt>
                  <dd className="mt-1 text-[var(--color-fg-muted)]">{q.reponse}</dd>
                </div>
              ))}
            </dl>
          </Carte>
        )}
      </div>

      <div className="flex items-center gap-2 text-[13px] text-[var(--color-fg-muted)]">
        {Icone && <Icone taille={15} />}
        <span>
          Cette page ne répond pas à votre question ?{' '}
          <LienAide publique={publique}>Écrivez au support.</LienAide>
        </span>
      </div>
    </div>
  );
}
